/**
 * GlobalSearch — trigger button shown in the app header.
 *
 * Opens the CommandPalette modal. The palette itself is rendered at the
 * _app layout level (see route.tsx) so it lives above the AppBackground
 * blur compositor layers, eliminating the black flash completely.
 */

import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { Search } from "@fleet/icons";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";
import { createSignal, onMount } from "solid-js";

export function GlobalSearch() {
  const { t } = useI18n();
  const { open } = useSearchPalette();
  const [isMac, setIsMac] = createSignal(false);

  onMount(() => {
    setIsMac(/Mac|iPod|iPhone|iPad/.test(navigator.userAgent));
  });

  return (
    <button
      type="button"
      id="global-search-trigger"
      class="flex items-center gap-2 h-9 px-3 rounded-[10px] border border-border/50 bg-muted/40 text-muted-foreground cursor-pointer transition-all duration-150 ease-out max-w-[320px] min-w-[36px] hover:bg-muted/70 hover:border-border/80 hover:text-foreground"
      onClick={() => {
        open();
      }}
      aria-label={t(msg`Open search`)}
    >
      <Search class="h-4 w-4 shrink-0 text-muted-foreground" />
      <span class="text-[13px] flex-1 text-left truncate tracking-[-0.01em] hidden sm:block">
        {t(msg`Search tasks or lists...`)}
      </span>
      <kbd class="py-0.5 px-1.5 rounded-[5px] border border-border/50 bg-background/60 font-mono text-[10px] font-semibold text-muted-foreground shrink-0 ml-auto hidden sm:inline-flex">
        {isMac() ? "⌘K" : "Ctrl+K"}
      </kbd>
    </button>
  );
}
